import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Select from "react-select";
import DatePicker from "react-datepicker";
import Loader from "../Loader";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Collapsible from "react-collapsible";
import { PROVIDER_POSITIONS } from "../enum";
import "../modal.css";
const basicSchema = Yup.object().shape({
  plateNumber: Yup.string().when("provider_type", (provider_type, schema) => {
    if (provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER) {
      return schema.notRequired();
    }
    return schema.required("Plate Number is required");
  }),
  model: Yup.string().when("provider_type", (provider_type, schema) => {
    if (provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER) {
      return schema.notRequired();
    }
    return schema.required("Vehicle Model is required");
  }),
  color: Yup.string().when("provider_type", (provider_type, schema) => {
    if (provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER) {
      return schema.notRequired();
    }
    return schema.required("Color is required");
  }),
  vehicleTypeId: Yup.string().required("Please select vehicle Type"),
  manufacturedYear: Yup.mixed()
    .nullable()
    .test('is-valid-date', 'Invalid date provided', function(value) {
      if (!value) return true; // null/undefined is ok
      
      try {
        const date = new Date(value);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return false;
        }
        
        // Check if date is reasonable (after 1900 and before current year + 1)
        const year = date.getFullYear();
        const currentYear = new Date().getFullYear();
        if (year < 1900 || year > currentYear + 1) {
          return this.createError({ message: `Year must be between 1900 and ${currentYear + 1}` });
        }
        
        return true;
      } catch (error) {
        return false;
      }
    })
    .when("provider_type", (provider_type, schema) => {
      if (provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER) {
        return schema.notRequired();
      }
      return schema.required("Manufactured Year is required");
    }),
  documents: Yup.mixed().when("provider_type", (provider_type, schema) => {
    if (provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER) {
      return schema.notRequired();
    }
    return schema.required("Document is required");
  }),
  vehicleImage: Yup.mixed().when("provider_type", (provider_type, schema) => {
    if (provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER) {
      return schema.notRequired();
    }
    return schema.required("Image is required");
  }),
  tradeLicence: Yup.mixed().when("provider_type", (provider_type, schema) => {
    if (provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER) {
      return schema.notRequired();
    }
    return schema.required("Trade Licence is required");
  }),
  insurance: Yup.mixed().when("provider_type", (provider_type, schema) => {
    if (provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER) {
      return schema.notRequired();
    }
    return schema.required("Insurance is required");
  }),
  provider_type: Yup.number().required("Provider Type is required"),
  driverId: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required("Driver ID missing"),
        label: Yup.string().required("Driver name missing"),
      })
    )
    .when("provider_type", (provider_type, schema) => {
      if (provider_type === PROVIDER_POSITIONS.AMBULANCE_PROVIDER) {
        return schema
          .min(1, "At least one driver must be selected")
          .required("Driver is required");
      } else {
        return schema
          .min(1, "Please select a driver")
          .max(1, "Only one driver allowed")
          .required("Driver is required");
      }
    }),
});

const VehicleForm = ({ mode, showModal, hideModal, initialData = {}, onSuccess }) => {
  const history = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [ambulanceCompanies, setAmbulanceCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const isCompanyUser = typeof window !== 'undefined' && localStorage.getItem('userType') === 'ambulance_company';
  
  // Function to sanitize date value
  const sanitizeDate = (dateValue) => {
    if (!dateValue) return "";
    
    try {
      const date = new Date(dateValue);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date detected, resetting to empty:', dateValue);
        return "";
      }
      
      // Check if date is reasonable (after 1900 and before current year + 1)
      const year = date.getFullYear();
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear + 1) {
        console.warn('Date out of reasonable range, resetting to empty:', dateValue, 'Year:', year);
        return "";
      }
      
      return dateValue;
    } catch (error) {
      console.error('Error sanitizing date, resetting to empty:', error, 'Input:', dateValue);
      return "";
    }
  };

  const [previewImages, setPreviewImages] = useState({
    vehicleImage: null,
    documents: null,
    tradeLicence: null,
    insurance: null,
  });

  const isEditMode = mode === "edit";

  const getDrivers = async (companyId) => {
    try {
      // For editing vehicles, use vehicle-specific endpoint that includes current drivers
      // For new vehicles, use general available drivers endpoint
      const base = isEditMode && initialData?.id 
        ? `/drivers/available/${initialData.id}` 
        : "/drivers/available";
      const endpoint = !isEditMode && companyId 
        ? `${base}?company_id=${encodeURIComponent(companyId)}` 
        : base;
      
      const response = await axios.get(endpoint);
      setDrivers(response.data || []);
    } catch (err) {
      setErrorMsg("Failed to load drivers.");
    }
  };

  const getVehicleTypes = async () => {
    try {
      const response = await axios.get("/vehicleTypes");
      if (isCompanyUser) {
        const allowedNames = new Set(["BLS Ambulance", "ALS Ambulance", "Free Ambulance"]);
        setVehicleTypes((response.data || []).filter(v => allowedNames.has(v.typeName)));
      } else {
        setVehicleTypes(response.data);
      }
    } catch (err) {
      setErrorMsg("Failed to load vehicle types.");
    }
  };


  function transformDriverData(driversFromBackend) {
    if (typeof driversFromBackend === "string") {
      try {
        driversFromBackend = JSON.parse(driversFromBackend);
      } catch (e) {
        driversFromBackend = [];
      }
    }
  
    return (driversFromBackend || []).map((d) => {
      let id = d.id;
      if (typeof id === "string" && id.startsWith('"') && id.endsWith('"')) {
        try {
          id = JSON.parse(id);
        } catch {}
      }
      return {
        value: id?.toString() ?? "",
        label: (d.firstName || "") + (d.lastName ? " " + d.lastName : "") + (d.phoneNumber ? ` (${d.phoneNumber})` : ""),
      };
    });
  }
  

  const onSubmit = async (values) => {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    let driverIds = [];
    if (Array.isArray(values.driverId)) {
      driverIds = values.driverId.map((d) => d.value.toString());
    } else {
      if (values.driverId?.value) {
        driverIds = [values.driverId.value.toString()];
      }
    }

    const bodyFormData = new FormData();
    bodyFormData.append("plateNumber", values.plateNumber || "");
    bodyFormData.append("model", values.model || "");
    bodyFormData.append("color", values.color || "");
    bodyFormData.append("vehicleTypeId", values.vehicleTypeId);
    bodyFormData.append("manufacturedYear", values.manufacturedYear || "");
    driverIds.forEach((id) => bodyFormData.append("driverId[]", id));

    if (!isCompanyUser && values.company_id) {
      bodyFormData.append("company_id", values.company_id);
    }

    // Handle optional file uploads for first responders
    if (values.documents) {
      bodyFormData.append("documents", values.documents);
    }
    if (values.vehicleImage) {
      bodyFormData.append("vehicleImage", values.vehicleImage);
    }
    if (values.tradeLicence) {
      bodyFormData.append("tradeLicence", values.tradeLicence);
    }
    if (values.insurance) {
      bodyFormData.append("insurance", values.insurance);
    }

    try {
      setLoading(true);

      if (isEditMode) {
        bodyFormData.append("id", initialData?.id);
        await axios.post(`/vehicle/${initialData?.id}`, bodyFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Vehicle updated successfully!");
      } else {
        await axios.post("/vehicles", bodyFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Vehicle added successfully!");
      }

      setLoading(false);
      onSuccess();
      hideModal();
    } catch (err) {
      setLoading(false);
      setErrorMsg(err.response?.data?.msg || "An error occurred");
      toast.error("Something went wrong...");
    }
  };


const handlePreview = async (fieldName) => {
  const currentValue = values[fieldName];

  if (currentValue instanceof File) {
    const localPreviewURL = URL.createObjectURL(currentValue);

    setPreviewImages((prev) => ({
      ...prev,
      [fieldName]: {
        url: localPreviewURL,
        type: currentValue.type, 
      },
    }));
    return;
  }

  if (typeof currentValue === "string") {
    let fileURL = "";

    if (currentValue.startsWith("http://") || currentValue.startsWith("https://")) {
      fileURL = currentValue;
    } else if (currentValue.startsWith("/")) {
      const backendHost =
        process.env.REACT_APP_BASE_URL
      fileURL = backendHost + currentValue;
    }

    if (fileURL) {
      try {

        const token = localStorage.getItem("d4uToken");
        const response = await axios.get(fileURL, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        });

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const blobURL = URL.createObjectURL(blob);

        setPreviewImages((prev) => ({
          ...prev,
          [fieldName]: {
            url: blobURL,
            type: blob.type, 
          },
        }));
      } catch (err) {
        console.error("Preview fetch error:", err);
      }
    }
  }
};

function FilePreview({ fileInfo }) {

  if (!fileInfo?.url || !fileInfo?.type) {
    return <p>No preview yet</p>;
  }

  if (fileInfo.type.startsWith("image/")) {
    return (
      <img
        src={fileInfo.url}
        alt="file preview"
        style={{ maxWidth: "300px", maxHeight: "300px" }}
      />
    );
  }

  if (fileInfo.type === "application/pdf") {
    return (
      <embed
        src={fileInfo.url}
        type="application/pdf"
        width="100%"
        height="600px"
      />
    );
  }

  return (
    <p>
      Preview not available.{" "}
      <a href={fileInfo.url} target="_blank" rel="noreferrer">
        Click here to open file in new tab
      </a>
    </p>
  );
}


  const DatePickerField = ({ name, value, onChange }) => {
    // Function to safely parse date
    const parseDate = (dateValue) => {
      if (!dateValue) return null;
      
      try {
        const date = new Date(dateValue);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          console.warn('Invalid date detected in DatePicker:', dateValue);
          return null;
        }
        
        // Check if date is reasonable (after 1900 and before 2100)
        const year = date.getFullYear();
        if (year < 1900 || year > 2100) {
          console.warn('Date out of reasonable range in DatePicker:', dateValue, 'Year:', year);
          return null;
        }
        
        return date;
      } catch (error) {
        console.error('Error parsing date in DatePicker:', error, 'Input:', dateValue);
        return null;
      }
    };
    
    return (
      <DatePicker
        selected={parseDate(value)}
        onChange={(val) => onChange(name, val)}
        dateFormat="yyyy-MM-dd"
        showYearPicker
        dateFormatCalendar="yyyy"
        minDate={new Date(1900, 0, 1)}
        maxDate={new Date(2100, 11, 31)}
      />
    );
  };


  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    resetForm,
    handleBlur,
    isSubmitting,
    submitForm,
  } = useFormik({
    initialValues: {
      plateNumber: initialData?.plateNumber || "",
      model: initialData?.model || "",
      color: initialData?.color || "",
      vehicleImage: initialData?.vehicleImage || "",
      vehicleTypeId: initialData?.vehicleTypeId || "",
      manufacturedYear: sanitizeDate(initialData?.manufacturedYear) || "",
      driverId: initialData?.drivers
      ? transformDriverData(initialData.drivers)
      : [],
      documents: initialData?.documents || "",
      tradeLicence: initialData?.tradeLicence || "",
      insurance: initialData?.insurance || "",
      provider_type: initialData?.provider_type || "" ,
      company_id: "",
    },
    validationSchema: basicSchema,
    onSubmit,
    enableReinitialize: true, 
  });


  useEffect(() => {
    if (showModal && !isEditMode) {
      resetForm();
    }
    getVehicleTypes();
    // Load companies for admin users only
    const fetchCompanies = async () => {
      if (!isCompanyUser) {
        try {
          const res = await axios.get("/ambulance-companies");
          setAmbulanceCompanies(res.data || []);
        } catch (e) {
          // non-fatal
        }
      }
    };
    fetchCompanies();
    // Initial driver load (optionally filtered by selected company)
    getDrivers(!isCompanyUser ? values.company_id : undefined);
    
  }, [showModal]);

  useEffect(() => {
    if (values.vehicleTypeId) {
      const selectedVehicleType = vehicleTypes.find(
        (vType) => vType.id.toString() === values.vehicleTypeId.toString()
      );
      if (selectedVehicleType) {
        setFieldValue("provider_type", selectedVehicleType.provider_type);
      } else {
        setFieldValue("provider_type", "");
      }
    } else {
      setFieldValue("provider_type", "");
    }

  }, [values.vehicleTypeId, vehicleTypes, setFieldValue]);

  // Refresh available drivers when selected company changes (admin add flow)
  useEffect(() => {
    if (!isCompanyUser && !isEditMode) {
      getDrivers(values.company_id);
    }
  }, [values.company_id, isCompanyUser, isEditMode]);

  
  return (
    <form  onSubmit={handleSubmit} autoComplete="off" encType="multipart/form-data">
      <Modal
        show={showModal}
        onHide={hideModal}
        backdrop={false}
        contentClassName="custom-modal-style"
        dialogClassName="custom-modal-dialog"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {mode === "edit" ? "Edit Vehicle" : "Add Vehicle"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div
              style={{
                position: "absolute",
                zIndex: "1000",
                top: "50%",
                left: "50%",
              }}
            >
              <Loader />
            </div>
          )}

          <div className="row">

            <div className="col-md-6">
              <div className="card card-primary">
                <div className="card-body">

                  <div className="form-group">
                    <label htmlFor="vehicleTypeId">Vehicle Type</label>
                    <select
                      id="vehicleTypeId"
                      name="vehicleTypeId"
                      value={values.vehicleTypeId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        errors.vehicleTypeId && touched.vehicleTypeId
                          ? "form-control input-error"
                          : "form-control"
                      }
                    >
                      <option value="">Select Vehicle Type</option>
                      {vehicleTypes.map((vType) => (
                        <option key={vType.id} value={vType.id}>
                          {vType.typeName}
                        </option>
                      ))}
                    </select>
                    {errors.vehicleTypeId && touched.vehicleTypeId && (
                      <p className="error">{errors.vehicleTypeId}</p>
                    )}
                  </div>

                  {!isCompanyUser && !isEditMode && (
                    <div className="form-group">
                      <label htmlFor="company_id">Ambulance Company (optional)</label>
                      <select
                        id="company_id"
                        name="company_id"
                        value={values.company_id}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFieldValue("company_id", val);
                        }}
                        className="form-control"
                      >
                        <option value="">Generic (no company)</option>
                        {ambulanceCompanies.map((c) => (
                          <option key={c.company_id} value={c.company_id}>
                            {c.company_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="plateNumber">
                      Plate Number
                      {values.provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER && (
                        <span style={{ color: "#6c757d", fontSize: "0.875em" }}> (Optional for First Responders)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="plateNumber"
                      value={values.plateNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        errors.plateNumber && touched.plateNumber
                          ? "form-control input-error"
                          : "form-control"
                      }
                    />
                    {errors.plateNumber && touched.plateNumber && (
                      <p className="error">{errors.plateNumber}</p>
                    )}
                  </div>


                  <div className="form-group">
                    <label htmlFor="model">
                      Model
                      {values.provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER && (
                        <span style={{ color: "#6c757d", fontSize: "0.875em" }}> (Optional for First Responders)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="model"
                      value={values.model}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        errors.model && touched.model
                          ? "form-control input-error"
                          : "form-control"
                      }
                    />
                    {errors.model && touched.model && (
                      <p className="error">{errors.model}</p>
                    )}
                  </div>


                  <div className="form-group">
                    <label htmlFor="color">
                      Color
                      {values.provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER && (
                        <span style={{ color: "#6c757d", fontSize: "0.875em" }}> (Optional for First Responders)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="color"
                      value={values.color}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        errors.color && touched.color
                          ? "form-control input-error"
                          : "form-control"
                      }
                    />
                    {errors.color && touched.color && (
                      <p className="error">{errors.color}</p>
                    )}
                  </div>


                  <div className="form-group">
                    <label htmlFor="manufacturedYear">
                      Manufactured Year
                      {values.provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER && (
                        <span style={{ color: "#6c757d", fontSize: "0.875em" }}> (Optional for First Responders)</span>
                      )}
                    </label>
                    <DatePickerField
                      name="manufacturedYear"
                      value={values.manufacturedYear}
                      onChange={setFieldValue}
                    />
                    {errors.manufacturedYear && touched.manufacturedYear && (
                      <p className="error">{errors.manufacturedYear}</p>
                    )}
                  </div>

                </div>
              </div>
            </div>


            <div className="col-md-6">
              <div className="card card-success">
                <div className="card-body">

                  {values.vehicleTypeId && (
                    <div className="form-group">
                      <label>
                        Select Driver 
                        {values.provider_type === PROVIDER_POSITIONS.AMBULANCE_PROVIDER ? "s" : ""}
                      </label>
                      <Select
                        isMulti={values.provider_type === PROVIDER_POSITIONS.AMBULANCE_PROVIDER}
                        name="driverId"
                        options={drivers.map((driver) => ({
                          value: driver.id.toString(),
                          label:
                            driver.firstName +
                            (driver.lastName ? " " + driver.lastName : "") +
                            (driver.phoneNumber ? ` (${driver.phoneNumber})` : ""),
                        }))}
                        value={
                          values.provider_type === PROVIDER_POSITIONS.AMBULANCE_PROVIDER
                            ? Array.isArray(values.driverId)
                              ? values.driverId
                              : []
                            : typeof values.driverId === "object"
                            ? values.driverId
                            : null
                        }
                        onChange={(selected) => {
                          if (!selected) {
                            setFieldValue("driverId", []);
                            return;
                          }

                          if (Array.isArray(selected)) {
                            setFieldValue("driverId", selected);
                          } else {
                            setFieldValue("driverId", [selected]); 
                          }
                        }}
                      />
                      {errors.driverId && touched.driverId && (
                        <p className="error">
                          {typeof errors.driverId === "string"
                            ? errors.driverId
                            : 
                              errors.driverId.min ||
                              "Driver selection is invalid"}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="vehicleImage">
                      Vehicle Image
                      {values.provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER && (
                        <span style={{ color: "#6c757d", fontSize: "0.875em" }}> (Optional for First Responders)</span>
                      )}
                    </label>
                    {values.vehicleImage && (
                      <Collapsible
                        trigger={
                          <i
                            className="fas fa-eye success"
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePreview("vehicleImage")}
                          >
                            {" "}
                            View
                          </i>
                        }
                      >
                          <FilePreview fileInfo={previewImages.vehicleImage} />     
                      </Collapsible>
                    )}
                    <div className="input-group">
                      <div className="custom-file">
                        <input
                          type="file"
                          name="vehicleImage"
                          accept="image/*"
                          onChange={(e) =>
                            setFieldValue("vehicleImage", e.currentTarget.files[0])
                          }
                          className={
                            errors.vehicleImage && touched.vehicleImage
                              ? "form-control input-error"
                              : "form-control"
                          }
                        />
                      </div>
                    </div>
                    {errors.vehicleImage && touched.vehicleImage && (
                      <p className="error">{errors.vehicleImage}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="documents">
                      Legal Documents
                      {values.provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER && (
                        <span style={{ color: "#6c757d", fontSize: "0.875em" }}> (Optional for First Responders)</span>
                      )}
                    </label>
                    {values.documents && (
                      <Collapsible
                        trigger={
                          <i
                            className="fas fa-eye success"
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePreview("documents")}
                          >
                            {" "}
                            View
                          </i>
                        }
                      >
                        <FilePreview fileInfo={previewImages.documents} />     

                      </Collapsible>
                    )}
                    <div className="input-group">
                      <div className="custom-file">
                        <input
                          type="file"
                          name="documents"
                          onChange={(e) =>
                            setFieldValue("documents", e.currentTarget.files[0])
                          }
                          onBlur={handleBlur}
                          className={
                            errors.documents && touched.documents
                              ? "form-control input-error"
                              : "form-control"
                          }
                        />
                      </div>
                    </div>
                    {errors.documents && touched.documents && (
                      <p className="error">{errors.documents}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="tradeLicence">
                      Trade Licence
                      {values.provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER && (
                        <span style={{ color: "#6c757d", fontSize: "0.875em" }}> (Optional for First Responders)</span>
                      )}
                    </label>
                    {values.tradeLicence && (
                      <Collapsible
                        trigger={
                          <i
                            className="fas fa-eye success"
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePreview("tradeLicence")}
                          >
                            {" "}
                            View
                          </i>
                        }
                      >
                         <FilePreview fileInfo={previewImages.tradeLicence} />     

                      </Collapsible>
                    )}
                    <div className="input-group">
                      <div className="custom-file">
                        <input
                          type="file"
                          name="tradeLicence"
                          onChange={(e) =>
                            setFieldValue("tradeLicence", e.currentTarget.files[0])
                          }
                          onBlur={handleBlur}
                          className={
                            errors.tradeLicence && touched.tradeLicence
                              ? "form-control input-error"
                              : "form-control"
                          }
                        />
                      </div>
                    </div>
                    {errors.tradeLicence && touched.tradeLicence && (
                      <p className="error">{errors.tradeLicence}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="insurance">
                      Insurance
                      {values.provider_type === PROVIDER_POSITIONS.FIRST_RESPONDER && (
                        <span style={{ color: "#6c757d", fontSize: "0.875em" }}> (Optional for First Responders)</span>
                      )}
                    </label>
                    {values.insurance && (
                      <Collapsible
                        trigger={
                          <i
                            className="fas fa-eye success"
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePreview("insurance")}
                          >
                            {" "}
                            View
                          </i>
                        }
                      >
                        <FilePreview fileInfo={previewImages.insurance} />       
                      </Collapsible>
                    )}
                    <div className="input-group">
                      <div className="custom-file">
                        <input
                          type="file"
                          name="insurance"
                          onChange={(e) =>
                            setFieldValue("insurance", e.currentTarget.files[0])
                          }
                          onBlur={handleBlur}
                          className={
                            errors.insurance && touched.insurance
                              ? "form-control input-error"
                              : "form-control"
                          }
                        />
                      </div>
                    </div>
                    {errors.insurance && touched.insurance && (
                      <p className="error">{errors.insurance}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          {errorMsg && (
            <div>
              <h5 className="error">{errorMsg}</h5>
            </div>
          )}
          <Button variant="default" onClick={hideModal}>
            Cancel
          </Button>
          <button
            disabled={isSubmitting}
            onClick={submitForm}
            className="btn btn-success"
            type="submit"
          >
            Submit
          </button>
        </Modal.Footer>
      </Modal>
    </form>
  );
};

export default VehicleForm;
